import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Sheet, ToastProvider, cn } from '@nexply/ui';
import {
  Home,
  Users,
  UsersRound,
  Plus,
  MoreHorizontal,
  Search as SearchIcon,
  UserPlus,
  Map as MapIcon,
  Sparkles,
  User,
  Settings,
  HardDrive,
  HelpCircle,
  Shield,
  MessageSquare,
  LogOut,
  Command,
} from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { Onboarding } from '../components/Onboarding.js';
import { FeedbackWidget } from '../components/FeedbackWidget.js';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Tab {
  to: string;
  label: string;
  icon: typeof Home;
  match?: string[];
}

const TABS: Tab[] = [
  { to: '/app', label: 'Home', icon: Home, match: ['/app'] },
  { to: '/members', label: 'Mitglieder', icon: Users, match: ['/members'] },
  { to: '/matches', label: 'Vernetzer', icon: Sparkles, match: ['/matches'] },
  { to: '/more', label: 'Mehr', icon: MoreHorizontal, match: ['/more', '/groups'] },
];

const TITLE_MAP: Record<string, string> = {
  '/app': 'Übersicht',
  '/more': 'Mehr',
  '/members': 'Mitglieder',
  '/matches': 'Vernetzer',
  '/map': 'Karte',
  '/groups': 'Netzwerk-Gruppen',
  '/profile': 'Mein Profil',
  '/settings': 'Einstellungen',
  '/backup': 'Backup',
  '/help': 'Hilfe',
  '/admin': 'Admin',
  '/admin/feedback': 'Feedback',
};

interface NavEntry { to: string; label: string; icon: typeof Home; }
interface NavSection { caption: string; entries: NavEntry[]; adminOnly?: boolean; }

const NAV: NavSection[] = [
  {
    caption: 'Meine Übersicht',
    entries: [{ to: '/app', label: 'Home', icon: Home }],
  },
  {
    caption: 'Netzwerk',
    entries: [
      { to: '/members', label: 'Mitglieder', icon: Users },
      { to: '/map', label: 'Karte', icon: MapIcon },
      { to: '/groups', label: 'Gruppen', icon: UsersRound },
      { to: '/matches', label: 'Vernetzer', icon: Sparkles },
    ],
  },
  {
    caption: 'Konto',
    entries: [
      { to: '/profile', label: 'Mein Profil', icon: User },
      { to: '/settings', label: 'Einstellungen', icon: Settings },
      { to: '/backup', label: 'Backup', icon: HardDrive },
      { to: '/help', label: 'Hilfe', icon: HelpCircle },
    ],
  },
  {
    caption: 'Admin',
    adminOnly: true,
    entries: [
      { to: '/admin', label: 'Admin', icon: Shield },
      { to: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
    ],
  },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [quickOpen, setQuickOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const title = useMemo(() => {
    const direct = TITLE_MAP[path];
    if (direct) return direct;
    for (const key of Object.keys(TITLE_MAP)) {
      if (key !== '/' && path.startsWith(key)) {
        const t = TITLE_MAP[key];
        if (t) return t;
      }
    }
    return 'Nexply';
  }, [path]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isAdmin = user?.role === 'ADMIN';
  const doLogout = async () => {
    try { await logout(); } catch {}
    window.location.assign('/login');
  };
  const { data: profileData } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => api.profile.me(),
    enabled: !!user,
  });
  const photoUrl = profileData?.member?.photoUrl ?? null;

  return (
    <ToastProvider>
      <div className="min-h-dvh bg-ink text-text">
        {/* Desktop sidebar */}
        <Sidebar path={path} isAdmin={isAdmin} userEmail={user?.email} userName={user?.name} photoUrl={photoUrl} onLogout={doLogout} />

        {/* Mobile top header */}
        <TopHeader
          title={title}
          onLogout={doLogout}
          userEmail={user?.email}
          photoUrl={photoUrl}
          onOpenSearch={() => setSearchOpen(true)}
        />

        <main className="lg:pl-sidebar pt-[calc(env(safe-area-inset-top)+60px)] lg:pt-[76px] pb-[calc(env(safe-area-inset-bottom)+96px)] lg:pb-10 min-h-dvh">
          <div className="mx-auto w-full max-w-2xl px-4 md:px-6 lg:max-w-content lg:px-8 lg:py-2">
            <Outlet />
          </div>
        </main>

        <Onboarding />

        <FeedbackWidget />

        {/* Mobile bottom-tabs + FAB */}
        <BottomTabs path={path} onPlus={() => setQuickOpen(true)} />

        <QuickActionSheet
          open={quickOpen}
          onClose={() => setQuickOpen(false)}
          onNav={(to) => { setQuickOpen(false); void nav({ to: to as any }); }}
        />
        <CommandPalette
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          isAdmin={isAdmin}
          onNav={(to) => { setSearchOpen(false); void nav({ to: to as any }); }}
        />
      </div>
    </ToastProvider>
  );
}

function Sidebar({
  path,
  isAdmin,
  userEmail,
  userName,
  photoUrl,
  onLogout,
}: {
  path: string;
  isAdmin: boolean;
  userEmail?: string;
  userName?: string | null;
  photoUrl?: string | null;
  onLogout: () => void;
}) {
  const sections = NAV.filter((s) => !s.adminOnly || isAdmin);
  const initial = (userName?.[0] ?? userEmail?.[0] ?? '?').toUpperCase();
  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-sidebar flex-col border-r border-line bg-surface-1">
      <div className="px-6 pt-7 pb-5">
        <div className="text-3xl font-semibold tracking-tighter text-gold-grad leading-none">nexply</div>
        <div className="mt-1.5 text-[10px] uppercase tracking-[0.35em] text-gold/60">Life is a Puzzle</div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {sections.map((sec) => (
          <div key={sec.caption} className="mt-4 first:mt-0">
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-text-muted">
              {sec.caption}
            </div>
            <ul className="space-y-0.5">
              {sec.entries.map((e) => {
                const active = isActiveRoute(path, e.to);
                const Icon = e.icon;
                return (
                  <li key={e.to}>
                    <Link
                      to={e.to}
                      className={cn(
                        'group relative flex items-center gap-3 rounded-xl pl-4 pr-3 py-2 text-[13.5px] transition-colors',
                        active
                          ? 'bg-surface-2 text-text'
                          : 'text-text-dim hover:bg-raise hover:text-text',
                      )}
                    >
                      {active && (
                        <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-gold" />
                      )}
                      <Icon
                        className={cn('h-[18px] w-[18px] shrink-0 transition-colors', active ? 'text-gold' : 'text-text-muted group-hover:text-text-dim')}
                        strokeWidth={active ? 2.2 : 1.8}
                      />
                      <span className="truncate">{e.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-line p-3">
        <div className="flex items-center gap-3 rounded-2xl bg-surface-2 p-3">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              className="h-9 w-9 shrink-0 rounded-full object-cover border border-line"
            />
          ) : (
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-gold-light to-gold-dark text-[#1A1714] text-sm font-semibold">
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-text truncate">{userName || userEmail || 'Konto'}</div>
            <div className="text-[11px] text-text-muted truncate">{userName ? userEmail : 'Angemeldet'}</div>
          </div>
          <button
            onClick={onLogout}
            className="grid h-8 w-8 place-items-center rounded-lg text-text-muted hover:bg-raise-2 hover:text-danger transition"
            aria-label="Abmelden"
            title="Abmelden"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function isActiveRoute(path: string, to: string): boolean {
  // '/admin' hat mit '/admin/feedback' einen eigenen Nav-Eintrag darunter –
  // sonst wären beide gleichzeitig aktiv.
  if (to === '/' || to === '/admin') return path === to;
  return path === to || path.startsWith(to + '/');
}

function TopHeader({
  title,
  onLogout,
  userEmail,
  photoUrl,
  onOpenSearch,
}: {
  title: string;
  onLogout: () => void;
  userEmail?: string;
  photoUrl?: string | null;
  onOpenSearch: () => void;
}) {
  const [openMenu, setOpenMenu] = useState(false);
  return (
    <header className="fixed top-0 left-0 right-0 z-30 glass border-b border-line pt-[env(safe-area-inset-top)] lg:pl-sidebar">
      <div className="mx-auto flex h-14 lg:h-16 max-w-2xl items-center justify-between px-4 md:px-6 lg:max-w-content lg:px-8">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-base lg:text-xl font-semibold tracking-tight text-text truncate">{title}</span>
        </div>

        <button
          onClick={onOpenSearch}
          className="hidden lg:flex items-center gap-2 h-9 w-[320px] xl:w-[420px] mx-6 rounded-xl border border-line bg-surface-2 hover:bg-surface-3 hover:border-line-strong px-3 text-left text-sm text-text-muted transition"
        >
          <SearchIcon className="h-4 w-4 text-text-muted shrink-0" />
          <span className="flex-1 truncate">Suchen oder springen …</span>
          <span className="flex items-center gap-1 text-[11px] text-text-muted">
            <Command className="h-3 w-3" /> K
          </span>
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={onOpenSearch}
            className="lg:hidden grid h-9 w-9 place-items-center rounded-full bg-surface-3 text-text-dim active:scale-95 transition"
            aria-label="Suchen"
          >
            <SearchIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setOpenMenu((o) => !o)}
            className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-surface-3 text-xs font-semibold text-gold active:scale-95 hover:border-gold/30 transition"
            aria-label="Konto"
          >
            {photoUrl ? (
              <img src={photoUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              userEmail?.[0]?.toUpperCase() ?? '?'
            )}
          </button>
        </div>
      </div>
      {openMenu && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpenMenu(false)} />
          <div className="absolute right-3 lg:right-8 top-[calc(env(safe-area-inset-top)+52px)] lg:top-[64px] z-40 w-56 rounded-2xl border border-line bg-surface-3 shadow-card-lg py-1.5 animate-fade-in">
            <div className="px-3 py-2 border-b border-line">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">Angemeldet</div>
              <div className="text-sm text-text truncate">{userEmail}</div>
            </div>
            <Link to="/profile" onClick={() => setOpenMenu(false)} className="block px-3 py-2.5 text-sm text-text-dim hover:bg-raise hover:text-text">
              Profil
            </Link>
            <Link to="/settings" onClick={() => setOpenMenu(false)} className="block px-3 py-2.5 text-sm text-text-dim hover:bg-raise hover:text-text">
              Einstellungen
            </Link>
            <button
              onClick={() => { setOpenMenu(false); onLogout(); }}
              className="block w-full text-left px-3 py-2.5 text-sm text-danger hover:bg-danger/10"
            >
              Abmelden
            </button>
          </div>
        </>
      )}
    </header>
  );
}

function BottomTabs({ path, onPlus }: { path: string; onPlus: () => void }) {
  function isActive(t: Tab) {
    if (!t.match) return path === t.to;
    if (t.to === '/' && path === '/') return true;
    return t.match.some((m) => (m === '/' ? path === '/' : path === m || path.startsWith(m + '/')));
  }

  const left = TABS.slice(0, 2);
  const right = TABS.slice(2);

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-line pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto flex max-w-2xl items-center justify-around px-2">
        {left.map((t) => <TabItem key={t.to} tab={t} active={isActive(t)} />)}
        <button
          onClick={onPlus}
          className="relative -mt-7 grid h-14 w-14 place-items-center rounded-full bg-gold-grad text-[#1A1714] shadow-glow active:scale-95 transition border-2 border-ink"
          aria-label="Schnellaktion"
        >
          <Plus className="h-6 w-6" strokeWidth={2.5} />
        </button>
        {right.map((t) => <TabItem key={t.to} tab={t} active={isActive(t)} />)}
      </div>
    </nav>
  );
}

function TabItem({ tab, active }: { tab: Tab; active: boolean }) {
  const Icon = tab.icon;
  return (
    <Link
      to={tab.to}
      className={cn(
        'relative flex h-14 w-16 flex-col items-center justify-center gap-0.5 transition active:scale-95',
        active ? 'text-gold' : 'text-text-muted',
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={active ? 2.4 : 1.8} />
      <span className={cn('text-[10px] font-medium', active && 'text-gold')}>{tab.label}</span>
      {active && <span className="absolute top-1 h-1 w-1 rounded-full bg-gold" />}
    </Link>
  );
}

interface QuickAction { to: string; label: string; sub: string; icon: ReactNode; }
const QUICK: QuickAction[] = [
  { to: '/members', label: 'Mitglied', sub: 'Neuen Kontakt anlegen', icon: <UserPlus className="h-5 w-5" /> },
  { to: '/matches', label: 'Vernetzer', sub: 'Vorschläge prüfen', icon: <Sparkles className="h-5 w-5" /> },
  { to: '/map', label: 'Karte', sub: 'Kontakte auf der Karte', icon: <MapIcon className="h-5 w-5" /> },
];

function QuickActionSheet({ open, onClose, onNav }: { open: boolean; onClose: () => void; onNav: (to: string) => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Schnellaktion" description="Wohin möchtest du springen?">
      <div className="grid grid-cols-2 gap-3 pt-1">
        {QUICK.map((q) => (
          <button
            key={q.to}
            onClick={() => onNav(q.to)}
            className="text-left rounded-2xl border border-line bg-surface-3 p-4 active:scale-[0.97] hover:border-gold/30 hover:bg-surface-3/80 transition"
          >
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gold/10 text-gold">
              {q.icon}
            </div>
            <div className="mt-3 text-[15px] font-semibold text-text">{q.label}</div>
            <div className="text-xs text-text-dim">{q.sub}</div>
          </button>
        ))}
      </div>
      <div className="pt-3" />
    </Sheet>
  );
}

interface PaletteEntry { to: string; label: string; section: string; }
const PALETTE: PaletteEntry[] = [
  { to: '/app', label: 'Übersicht / Home', section: 'Übersicht' },
  { to: '/members', label: 'Mitglieder', section: 'Netzwerk' },
  { to: '/members', label: 'Smart Search', section: 'Netzwerk' },
  { to: '/map', label: 'Karte', section: 'Netzwerk' },
  { to: '/groups', label: 'Netzwerk-Gruppen', section: 'Netzwerk' },
  { to: '/matches', label: 'Vernetzer', section: 'Netzwerk' },
  { to: '/profile', label: 'Mein Profil', section: 'Konto' },
  { to: '/settings', label: 'Einstellungen', section: 'Konto' },
  { to: '/backup', label: 'Backup', section: 'Konto' },
  { to: '/help', label: 'Hilfe', section: 'Konto' },
];

function CommandPalette({
  open,
  onClose,
  onNav,
  isAdmin,
}: {
  open: boolean;
  onClose: () => void;
  onNav: (to: string) => void;
  isAdmin: boolean;
}) {
  const [q, setQ] = useState('');
  useEffect(() => { if (!open) setQ(''); }, [open]);
  const entries = useMemo(() => {
    const all = isAdmin
      ? [
          ...PALETTE,
          { to: '/admin', label: 'Admin', section: 'Admin' },
          { to: '/admin/feedback', label: 'Feedback', section: 'Admin' },
        ]
      : PALETTE;
    const t = q.trim().toLowerCase();
    if (!t) return all;
    return all.filter((e) => e.label.toLowerCase().includes(t) || e.section.toLowerCase().includes(t));
  }, [q, isAdmin]);

  return (
    <Sheet open={open} onClose={onClose} title="Springe zu …" size="md">
      <div className="space-y-3">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          placeholder="Route, Bereich oder Aktion …"
          className="w-full h-11 rounded-xl border border-line bg-overlay px-4 text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-gold/60"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && entries[0]) onNav(entries[0].to);
          }}
        />
        <div className="max-h-[50vh] overflow-y-auto -mx-1">
          {entries.length === 0 && (
            <div className="text-xs text-text-muted text-center py-6">Keine Treffer.</div>
          )}
          {entries.map((e) => (
            <button
              key={e.to}
              onClick={() => onNav(e.to)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-raise text-left transition"
            >
              <span className="text-sm text-text">{e.label}</span>
              <span className="text-[10px] uppercase tracking-wider text-text-muted">{e.section}</span>
            </button>
          ))}
        </div>
      </div>
    </Sheet>
  );
}
